/* eslint-disable react/no-unstable-nested-components */
import { Box, Button, CircularProgress, List, Typography } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { SongListItemVariant } from 'components/SongList/components/SongListItem/SongListItemTypes';
import { useSongsStore, useUserStore } from 'services/hooks';
import { multiplesOf } from 'services/store/helpers/getMultiple';
import type { RootState } from 'services/store';
import theme from 'theme';
import {
  SongListItemContainer,
  SongsLibraryListItemContainer,
} from './components';
import useStyles from './style';

type PropsType = {
  isGetSongsLoading: boolean;
  onSongsLibraryPage?: boolean;
  songIds: Array<string>;
};

const SongList = ({
  isGetSongsLoading,
  onSongsLibraryPage,
  songIds,
}: PropsType) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { getSongs: getSongsFromSongStore, songsStore } = useSongsStore();
  const { getSongs: getSongsFromUserStore, userStore } = useUserStore();

  const tags = useSelector((store: RootState) => store.tags);
  const { page: songStorePage } = songsStore;
  const { page: userStorePage } = userStore.songsLibrary;

  const { search } = useLocation();
  const paginationMultiples = multiplesOf(songIds.length);
  // ----------- RENDER -----------
  const displayEmptySongListPlaceholder = () => {
    const placeholderText = onSongsLibraryPage
      ? t('songList:placeholder:emptyList')
      : t('songList:placeholder:main');
    return (
      <Typography className={classes.placeholder} variant="h4">
        {placeholderText}
      </Typography>
    );
  };

  const requestMoreSongs = () => {
    if (onSongsLibraryPage) {
      getSongsFromUserStore(userStorePage);
      return;
    }
    const params = {
      page: songStorePage + 1,
      selectedTags: tags.selectedTags,
      appliedFilters: tags.appliedFilters,
    };

    getSongsFromSongStore(params);
  };

  const displaySongListItems = () =>
    songIds.map((songId: string, index: number) =>
      onSongsLibraryPage ? (
        <SongsLibraryListItemContainer
          // @ts-ignore
          variant={SongListItemVariant.Library}
          key={songId}
          songId={songId}
          index={index}
          songIdsLength={songIds.length}
        />
      ) : (
        <SongListItemContainer
          variant={SongListItemVariant.Default}
          key={songId}
          songId={songId}
          index={index}
          songIdsLength={songIds.length}
          isFiltered={search.length}
        />
      )
    );

  const LoadMoreItems = () => (
    <Box className={classes.buttonContainer}>
      <Button variant="outlined" onClick={() => requestMoreSongs()}>
        {t('songList:loadMore')}
      </Button>
    </Box>
  );

  const displaySongList = () => {
    if (songIds.length === 0 && !isGetSongsLoading) {
      return displayEmptySongListPlaceholder();
    }
    return displaySongListItems();
  };

  return (
    <Box className={classes.root}>
      <List className={classes.list} data-testid="listContainer">
        {displaySongList()}
        {isGetSongsLoading && (
          <div data-testid="loadingItem">
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: isMobile ? theme.spacing(2) : theme.spacing(4),
                top: isMobile ? theme.spacing(15) : theme.spacing(20),
              }}
            >
              <CircularProgress size={55} />
            </Box>
          </div>
        )}
      </List>
      {!isGetSongsLoading && paginationMultiples && <LoadMoreItems />}
    </Box>
  );
};

SongList.defaultProps = {
  onSongsLibraryPage: false,
};

export default SongList;
